import { ReactNode } from 'react';
import { Button, Colors } from '@mercator-gcp/grout';
import { FlexRow } from 'components/layout/FlexRow';
import { ReactComponent as DoubleRightChevron } from 'icons/double-right-shevron.svg';
import { useFlyoutNavigation } from 'state/flyoutNavigation';
import { scrollBarPrefixRemoval } from 'utils/styles';

const containerStyle = { height: '100%', width: '100%' };
const menuStyle = (flyoutNavigationOpen: boolean) => ({
  minWidth: '250px',
  display: flyoutNavigationOpen ? 'block' : 'none',
  alignItems: 'normal',
  backgroundColor: Colors.Black800,
  height: '100%',
  overflowY: 'auto',
  ...scrollBarPrefixRemoval,
});

const detailWrapperStyle = {
  position: 'relative' as const,
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  overflow: 'auto',
  width: '100%',
  height: '100%',
  padding: 'padding_32',
  paddingBottom: 'padding_16',
  boxSizing: 'border-box',
};

const reopenButtonStyle = {
  position: 'absolute' as const,
  left: 0,
  top: 'padding_16', // Align with FlyoutMenu header where collapse button sits
  zIndex: 1,
  minWidth: 'auto',
  width: '20px',
  height: '40px',
  paddingLeft: '2px',
  paddingRight: '2px',
  paddingTop: 0,
  paddingBottom: 0,
  border: '2px solid',
  borderLeft: 'none',
  borderColor: Colors.ForestGreen900,
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  borderTopRightRadius: '8px',
  borderBottomRightRadius: '8px',
  backgroundColor: 'transparent',
  '&:hover': {
    borderColor: Colors.ForestGreen900,
    backgroundColor: 'transparent',
  },
  '& svg': {
    transform: 'scaleX(-1)', // Double-right chevron (>>) to expand panel
  },
};

type TLayoutProps = {
  menuContent?: ReactNode | null;
  detailContent: ReactNode;
};

const Layout = ({ menuContent, detailContent }: TLayoutProps) => {
  const { flyoutNavigationOpen, updateFlyoutNavigationStatus } = useFlyoutNavigation();
  const showReopenPanel = !!menuContent && !flyoutNavigationOpen;

  return (
    <FlexRow sx={containerStyle}>
      {menuContent && (
        <FlexRow.Left sx={menuStyle(flyoutNavigationOpen)}>{menuContent}</FlexRow.Left>
      )}
      <FlexRow.Right sx={detailWrapperStyle}>
        {showReopenPanel && (
          <Button
            variant="functional"
            onClick={() => updateFlyoutNavigationStatus(true)}
            sx={reopenButtonStyle}
            aria-label="Reopen navigation panel"
          >
            <DoubleRightChevron />
          </Button>
        )}
        {detailContent}
      </FlexRow.Right>
    </FlexRow>
  );
};

export default Layout;
